import React, { useState, useEffect, useCallback } from 'react';
import { UserInfo } from '../types';
import { apiCall } from '../auth';

interface ManagedUser {
  login:        string;
  fullName:     string;
  isAdmin:      boolean;
  isProtected:  boolean;
  canPicLayers: boolean;
}

interface Props {
  currentUser: UserInfo;
  token:       string;
  onLogout:    () => void;
}

type DialogMode = 'none' | 'add' | 'resetPassword';

const AdminPanel: React.FC<Props> = ({ currentUser, token, onLogout }) => {
  const [users,      setUsers]      = useState<ManagedUser[]>([]);
  const [dialog,     setDialog]     = useState<DialogMode>('none');
  const [targetUser, setTargetUser] = useState('');   // dla resetPassword
  const [feedback,   setFeedback]   = useState<{ msg: string; ok: boolean } | null>(null);

  // Pola formularzy
  const [fLogin,  setFLogin]  = useState('');
  const [fName,   setFName]   = useState('');
  const [fPass,   setFPass]   = useState('');
  const [fPass2,  setFPass2]  = useState('');
  const [fError,  setFError]  = useState('');
  const [fBusy,   setFBusy]   = useState(false);

  const call = useCallback(
    <T,>(method: string, path: string, body?: unknown) =>
      apiCall<T>(method, path, token, body),
    [token],
  );

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await call<ManagedUser[]>('GET', '/api/users'));
    } catch (e: any) {
      setFeedback({ msg: e.message, ok: false });
    }
  }, [call]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const showFeedback = (msg: string, ok = true) => {
    setFeedback({ msg, ok });
    setTimeout(() => setFeedback(null), 4000);
  };

  const closeDialog = () => {
    setDialog('none'); setTargetUser('');
    setFLogin(''); setFName(''); setFPass(''); setFPass2(''); setFError('');
  };

  const openReset = (login: string) => {
    setTargetUser(login);
    setFPass(''); setFPass2(''); setFError('');
    setDialog('resetPassword');
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fPass !== fPass2)   { setFError('Hasła się nie zgadzają.');       return; }
    if (fPass.length < 6)   { setFError('Hasło musi mieć min. 6 znaków.'); return; }
    setFBusy(true); setFError('');
    try {
      await call('POST', '/api/users', { login: fLogin.trim(), fullName: fName.trim(), password: fPass });
      showFeedback(`Dodano użytkownika: ${fLogin.trim()}`);
      closeDialog();
      await loadUsers();
    } catch (e: any) {
      setFError(e.message);
    } finally {
      setFBusy(false);
    }
  };

  const handleDelete = async (login: string) => {
    if (!window.confirm(`Usunąć użytkownika „${login}"?`)) return;
    try {
      await call('DELETE', `/api/users/${login}`);
      showFeedback(`Usunięto użytkownika: ${login}`);
      await loadUsers();
    } catch (e: any) {
      showFeedback(e.message, false);
    }
  };

  const handleTogglePermission = async (login: string, permission: string, value: boolean) => {
    try {
      await call('PUT', `/api/users/${login}/permissions`, { permission, value });
      await loadUsers();
    } catch (e: any) {
      showFeedback(e.message, false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fPass !== fPass2)   { setFError('Hasła się nie zgadzają.');       return; }
    if (fPass.length < 6)   { setFError('Hasło musi mieć min. 6 znaków.'); return; }
    setFBusy(true); setFError('');
    try {
      await call('PUT', `/api/users/${targetUser}/password`, { password: fPass });
      showFeedback(`Zmieniono hasło dla: ${targetUser}`);
      closeDialog();
    } catch (e: any) {
      setFError(e.message);
    } finally {
      setFBusy(false);
    }
  };

  return (
    <div className="admin-root">
      {/* ── Nagłówek ── */}
      <div className="admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">PDD</span>
          <span className="admin-title">Panel zarządzania użytkownikami</span>
        </div>
        <div className="admin-header-right">
          <span className="admin-who">
            Zalogowany: <strong>{currentUser.fullName}</strong>
          </span>
          <button className="btn-logout" onClick={onLogout}>Wyloguj</button>
        </div>
      </div>

      {/* ── Komunikat zwrotny ── */}
      {feedback && (
        <div className={`admin-feedback ${feedback.ok ? 'ok' : 'err'}`}
          onClick={() => setFeedback(null)}>
          {feedback.msg} &nbsp;<span className="close-x">✕</span>
        </div>
      )}

      {/* ── Tabela użytkowników ── */}
      <div className="admin-body">
        <table className="users-table">
          <thead>
            <tr>
              <th>Login</th>
              <th>Pełna nazwa</th>
              <th>Rola</th>
              <th className="th-perm">P.Lay</th>
              <th>Akcje</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.login}>
                <td className="td-login">{u.login}</td>
                <td>{u.fullName}</td>
                <td className="td-role">{u.isAdmin ? '👑 Admin' : 'Użytkownik'}</td>
                <td className="td-perm">
                  {!u.isAdmin && (
                    <input
                      type="checkbox"
                      title="Pic Layers"
                      checked={u.canPicLayers}
                      onChange={e => handleTogglePermission(u.login, 'canPicLayers', e.target.checked)}
                    />
                  )}
                </td>
                <td className="td-actions">
                  <button className="btn-sm btn-reset-pw"
                    onClick={() => openReset(u.login)}>
                    Resetuj hasło
                  </button>
                  {!u.isProtected && (
                    <button className="btn-sm btn-del"
                      onClick={() => handleDelete(u.login)}>
                      Usuń
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <button className="btn-add-user" onClick={() => setDialog('add')}>
          + Dodaj użytkownika
        </button>
      </div>

      {/* ── Modal: Dodaj użytkownika ── */}
      {dialog === 'add' && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) closeDialog(); }}>
          <div className="modal-card">
            <div className="modal-title">Dodaj użytkownika</div>
            <form onSubmit={handleAdd}>
              <label className="login-label">Login
                <input className="login-input" value={fLogin} required
                  autoFocus autoComplete="off"
                  onChange={e => setFLogin(e.target.value)} />
              </label>
              <label className="login-label">Pełna nazwa
                <input className="login-input" value={fName} required
                  autoComplete="off"
                  onChange={e => setFName(e.target.value)} />
              </label>
              <label className="login-label">Hasło
                <input className="login-input" type="password" value={fPass} required
                  autoComplete="new-password"
                  onChange={e => setFPass(e.target.value)} />
              </label>
              <label className="login-label">Powtórz hasło
                <input className="login-input" type="password" value={fPass2} required
                  autoComplete="new-password"
                  onChange={e => setFPass2(e.target.value)} />
              </label>
              {fError && <div className="login-error">{fError}</div>}
              <div className="modal-btns">
                <button type="submit" disabled={fBusy}>{fBusy ? '…' : 'Dodaj'}</button>
                <button type="button" onClick={closeDialog}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Resetuj hasło ── */}
      {dialog === 'resetPassword' && (
        <div className="modal-overlay" onMouseDown={e => { if (e.target === e.currentTarget) closeDialog(); }}>
          <div className="modal-card">
            <div className="modal-title">Resetuj hasło</div>
            <div className="modal-subtitle">Użytkownik: <strong>{targetUser}</strong></div>
            <form onSubmit={handleResetPassword}>
              <label className="login-label">Nowe hasło
                <input className="login-input" type="password" value={fPass} required
                  autoFocus autoComplete="new-password"
                  onChange={e => setFPass(e.target.value)} />
              </label>
              <label className="login-label">Powtórz hasło
                <input className="login-input" type="password" value={fPass2} required
                  autoComplete="new-password"
                  onChange={e => setFPass2(e.target.value)} />
              </label>
              {fError && <div className="login-error">{fError}</div>}
              <div className="modal-btns">
                <button type="submit" disabled={fBusy}>{fBusy ? '…' : 'Zapisz'}</button>
                <button type="button" onClick={closeDialog}>Anuluj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
